import { isPlatformHost } from './platform-origin.utils';

/**
 * ONE home for the silent sign-in hint the boot decision reads. On platform hosts the auth server
 * writes `branduals_sso=1` on `.branduals.com` at every sign-in, and the SPA only reads it. An
 * custom domain can never see that cookie (another registrable domain), so the SPA on a
 * custom domain keeps its OWN host-only copy: written when a sign-in lands on this origin, cleared
 * on sign-out and when a silent probe comes back declined. It is a 1-bit "worth probing" flag, never
 * a credential — the auth server still decides every probe, and a stale copy costs one declined
 * round trip before the declined leg clears it.
 *
 * The SPA never writes or clears the hint on a platform host: there the auth server owns it, and a
 * host-only duplicate would survive the server's sign-out clear.
 */
export const SSO_HINT_COOKIE = 'branduals_sso';

/**
 * The hint's URL form: a signed-in platform hop (the Account app opening a brand on its custom
 * domain) appends `sso=1`, because the arriving origin has no hint of its own yet. The brand app
 * treats it as the hint for that one boot and strips it.
 */
export const SSO_HANDOFF_PARAM = 'sso';

/** The auth server's longest hint lifetime (a remembered sign-in), mirrored so both hints age alike. */
const SSO_HINT_MAX_AGE_SECONDS = 60 * 24 * 60 * 60;

/** The cookie sink — `document` in the app; a plain object in specs. */
type CookieSink = { cookie: string };

export function hasSsoHint(cookie: string = document.cookie): boolean {
  return cookie.split('; ').some(entry => entry === `${SSO_HINT_COOKIE}=1`);
}

export function writeCustomDomainSsoHint(hostname: string = window.location.hostname, sink: CookieSink = document): void {
  if (isPlatformHost(hostname)) return;
  sink.cookie = `${SSO_HINT_COOKIE}=1; Path=/; Max-Age=${SSO_HINT_MAX_AGE_SECONDS}; Secure; SameSite=Lax`;
}

export function clearCustomDomainSsoHint(hostname: string = window.location.hostname, sink: CookieSink = document): void {
  if (isPlatformHost(hostname)) return;
  sink.cookie = `${SSO_HINT_COOKIE}=; Path=/; Max-Age=0; Secure; SameSite=Lax`;
}

export function hasSsoHandoff(search: string): boolean {
  return new URLSearchParams(search).get(SSO_HANDOFF_PARAM) === '1';
}

/**
 * The same path + query + fragment with the handoff parameter removed; returned untouched when the
 * parameter is absent, so ordinary URLs keep their exact encoding.
 */
export function withoutSsoHandoff(pathAndQuery: string): string {
  const url = new URL(pathAndQuery, 'https://placeholder.invalid');
  if (!url.searchParams.has(SSO_HANDOFF_PARAM)) return pathAndQuery;
  url.searchParams.delete(SSO_HANDOFF_PARAM);
  return url.pathname + url.search + url.hash;
}
